import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, MenuItem, Typography, Divider, CircularProgress, Alert,
  Box, InputAdornment
} from '@mui/material';
import axios from 'axios';

import { ASIGNACIONES_API } from '../apiConfig';
const API_BASE_URL = ASIGNACIONES_API.replace(/\/asignaciones$/, '');

const ESTADOS_ASIGNACION = ['Activo', 'Programado', 'Finalizado', 'Cancelado'];

export default function AsignacionFormModal({ open, onClose, onSaved, initialData, espacios, readOnly = false }) {
  const isEdit = Boolean(initialData?.id);
  const [form, setForm] = useState({
    codigo_espacio: '',
    cliente: '',
    fecha_inicio: '',
    fecha_fin: '',
    tipo_venta: 'Cara Fija',
    estado: 'Programado',
    monto_total: '',
    detalles_json: {}
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      if (initialData) {
        setForm({
          codigo_espacio: initialData.codigo_espacio || '',
          cliente: initialData.cliente || '',
          fecha_inicio: initialData.fecha_inicio || '',
          fecha_fin: initialData.fecha_fin || '',
          tipo_venta: initialData.tipo_venta || 'Cara Fija',
          estado: initialData.estado || 'Programado',
          monto_total: initialData.monto_total || '',
          detalles_json: initialData.detalles_json || {}
        });
      } else {
        setForm({
          codigo_espacio: '',
          cliente: '',
          fecha_inicio: '',
          fecha_fin: '',
          tipo_venta: 'Cara Fija',
          estado: 'Programado',
          monto_total: '',
          detalles_json: {}
        });
      }
      setError('');
    }
  }, [open, initialData]);

  const set = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const setDetalle = (field) => (e) => {
    setForm(prev => ({
      ...prev,
      detalles_json: { ...prev.detalles_json, [field]: e.target.value }
    }));
  };

  // Determine current Espacio type category
  const selectedEspacio = espacios.find(e => e.Codigo === form.codigo_espacio);
  const isDigital = selectedEspacio && (selectedEspacio.Tipo_Espacio === 'Digital' || selectedEspacio.Tipo_Espacio?.includes('Pantalla') || selectedEspacio.Formato === 'Digital');
  const isUrbano = selectedEspacio && (selectedEspacio.Tipo_Espacio === 'Mobiliario Urbano' || selectedEspacio.Formato?.includes('Mobiliario'));
  const isFisica = selectedEspacio && !isDigital && !isUrbano;

  // Auto-update tipo_venta based on space logic whenever space changes
  useEffect(() => {
    if (!initialData && selectedEspacio) {
      if (isDigital) setForm(prev => ({ ...prev, tipo_venta: 'Bloque Horario' }));
      else if (isUrbano) setForm(prev => ({ ...prev, tipo_venta: 'Paquete Catorcenal' }));
      else setForm(prev => ({ ...prev, tipo_venta: 'Cara Fija' }));
    }
  }, [form.codigo_espacio, isDigital, isUrbano, initialData, selectedEspacio]);

  const handleSave = async () => {
    if (!form.codigo_espacio || !form.cliente || !form.fecha_inicio || !form.fecha_fin) {
      setError('Llene los campos obligatorios: Espacio, Cliente, Fechas.'); return;
    }
    if (form.fecha_inicio > form.fecha_fin) {
      setError('La fecha de fin no puede ser anterior a la fecha de inicio.'); return;
    }

    setSaving(true);
    setError('');
    try {
      const payload = { ...form };
      if (payload.monto_total === '') payload.monto_total = null;

      if (isEdit) {
        await axios.patch(`${API_BASE_URL}/asignaciones/${initialData.id}`, payload);
      } else {
        await axios.post(`${API_BASE_URL}/asignaciones`, payload);
      }

      onSaved?.();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Error al guardar la asignación');
    } finally {
      setSaving(false);
    }
  };

  const currentDetalles = form.detalles_json || {};

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, borderBottom: '1px solid', borderColor: 'divider' }}>
        {readOnly ? '📄 Ver Contrato' : isEdit ? '📝 Editar Contrato' : '🤝 Nuevo Contrato / Asignación'}
      </DialogTitle>

      <DialogContent dividers sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1 }}>
          Datos Generales
        </Typography>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <TextField 
              label="Valla / Espacio" size="small" fullWidth select 
              value={form.codigo_espacio} onChange={set('codigo_espacio')}
              disabled={isEdit || readOnly}
            >
              {espacios.map(e => (
                <MenuItem key={e.Codigo} value={e.Codigo}>
                  <b>{e.Codigo}</b> - {e.Tipo_Espacio} {e.Formato ? `(${e.Formato})` : ''} - {e.Provincia}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Nombre del Cliente" size="small" fullWidth value={form.cliente} onChange={set('cliente')} placeholder="Ej: Coca-Cola, Claro..." disabled={readOnly} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Fecha Inicio" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.fecha_inicio} onChange={set('fecha_inicio')} disabled={readOnly} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Fecha Fin" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={form.fecha_fin} onChange={set('fecha_fin')} disabled={readOnly} />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Estado del Contrato" size="small" fullWidth select value={form.estado} onChange={set('estado')} disabled={readOnly}>
              {ESTADOS_ASIGNACION.map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <TextField 
              label="Monto Facturado" size="small" fullWidth type="number" 
              value={form.monto_total} onChange={set('monto_total')} 
              InputProps={{ startAdornment: <InputAdornment position="start">RD$</InputAdornment> }}
              disabled={readOnly}
            />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        {/* Dynamic Section Based on Billboard Type */}
        <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1 }}>
          Detalles de Asignación {selectedEspacio && `(${selectedEspacio.Tipo_Espacio})`}
        </Typography>

        {(!selectedEspacio) && (
          <Typography variant="body2" color="text.secondary">
            Selecciona una valla primero para ver las opciones avanzadas...
          </Typography>
        )}

        {isFisica && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField 
                label="Tipo de Venta" size="small" fullWidth select 
                value={form.tipo_venta} onChange={set('tipo_venta')}
                disabled={readOnly}
              >
                <MenuItem value="Cara Fija">Cara Fija (A o B)</MenuItem>
                <MenuItem value="Estructura Completa">Estructura Completa</MenuItem>
              </TextField>
            </Grid>
            {form.tipo_venta === 'Cara Fija' && (
              <Grid item xs={12}>
                <TextField 
                  label="¿Cuál Cara?" size="small" fullWidth select 
                  value={currentDetalles.cara || ''} onChange={setDetalle('cara')}
                  disabled={readOnly}
                >
                  <MenuItem value="Cara A (Norte)">Cara A (Vista Norte)</MenuItem>
                  <MenuItem value="Cara B (Sur)">Cara B (Vista Sur)</MenuItem>
                  <MenuItem value="Cara C (Este)">Cara C (Vista Este)</MenuItem>
                  <MenuItem value="Cara D (Oeste)">Cara D (Vista Oeste)</MenuItem>
                </TextField>
              </Grid>
            )}
          </Grid>
        )}

        {isDigital && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField 
                label="Tipo de Venta (Digital)" size="small" fullWidth select 
                value={form.tipo_venta} onChange={set('tipo_venta')}
                disabled={readOnly}
              >
                <MenuItem value="Bloque Horario">Bloque Horario (Ej: Circuito 1, 2...)</MenuItem>
                <MenuItem value="Loop Compartido">Loop Compartido (Rotando 24/7)</MenuItem>
                <MenuItem value="Exclusividad">Exclusividad Pantalla 24/7</MenuItem>
              </TextField>
            </Grid>
            {form.tipo_venta === 'Bloque Horario' && (
              <Grid item xs={12}>
                <TextField 
                  label="Especifica el Bloque u Horario" size="small" fullWidth 
                  placeholder="Ej: Bloque 1 (5:00AM - 2:00PM)"
                  value={currentDetalles.bloque_horario || ''} onChange={setDetalle('bloque_horario')}
                  disabled={readOnly}
                />
              </Grid>
            )}
            {form.tipo_venta === 'Loop Compartido' && (
              <Grid item xs={6}>
                <TextField 
                  label="Segundos por Loop" size="small" fullWidth type="number"
                  placeholder="Ej: 8"
                  value={currentDetalles.segundos_loop || ''} onChange={setDetalle('segundos_loop')}
                  disabled={readOnly}
                />
              </Grid>
            )}
          </Grid>
        )}

        {isUrbano && (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField 
                label="Tipo de Contratación" size="small" fullWidth select 
                value={form.tipo_venta} onChange={set('tipo_venta')}
                disabled={readOnly}
              >
                <MenuItem value="Paquete Catorcenal">Paquete Catorcenal (Inversión Volumen)</MenuItem>
                <MenuItem value="Mobiliario Unitario">Mobiliario Unitario</MenuItem>
              </TextField>
            </Grid>
            {form.tipo_venta === 'Paquete Catorcenal' && (
              <Grid item xs={6}>
                <TextField 
                  label="Cantidad de Señales Negociadas" size="small" fullWidth type="number"
                  value={currentDetalles.cantidad_senales || ''} onChange={setDetalle('cantidad_senales')}
                  disabled={readOnly}
                />
              </Grid>
            )}
          </Grid>
        )}

      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={saving}>{readOnly ? 'Cerrar' : 'Cancelar'}</Button>
        {!readOnly && (
          <Button
            variant="contained" onClick={handleSave} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}
          >
            {saving ? 'Guardando...' : isEdit ? 'Actualizar Contrato' : 'Generar Contrato'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
